import { Dialog, Transition, TransitionChild , DialogPanel , DialogTitle } from "@headlessui/react";
import { Fragment, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";

const ChangePasswordDialog = ({ isOpen, onClose }) => {
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(6, "New password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your new password"),
  });


  const handleChangePassword = async (values) => {
     
      const payload = {
        password: values?.newPassword,
        confirm_password: values?.confirmPassword,
      };
      try {
        const response = await axios.post(
          "https://api.upswap.app/api/update-password/v2/",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          }
        );
  
        if (response.status === 200) {
          toast.success("Password has been updated successfully.");
        } else {
          throw new Error(response.data.detail || "Failed to update Password");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to update Password.");
      } finally {
        onClose();
      }
    };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          leave="ease-in duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center px-4 py-8">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              leave="ease-in duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="text-xl font-semibold text-gray-900 flex justify-between items-center"
                >
                  Change Password
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-800 text-xl font-bold"
                  >
                    &times;
                  </button>
                </DialogTitle>

                <Formik
                  initialValues={{
                    newPassword: "",
                    confirmPassword: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting }) => {
                   handleChangePassword(values)
                  }}
                >
                  {({ isSubmitting }) => (
                    <Form className="mt-4 space-y-4">
                      {[
                        { name: "newPassword", label: "New Password" },
                        { name: "confirmPassword", label: "Confirm New Password" },
                      ].map(({ name, label }) => (
                        <div key={name}>
                          <label className="block text-sm font-medium text-gray-700">
                            {label}
                          </label>
                          <div className="relative mt-1">
                            <Field
                              type={showPassword[name.includes("confirm") ? "confirm" : name.includes("new") ? "new" : "current"] ? "text" : "password"}
                              name={name}
                              className="w-full rounded border-2 px-4 py-2 pr-10  outline-none focus:border-orange-500 focus:ring-orange-500"
                              placeholder={`Enter ${label.toLowerCase()}`}
                            />
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                              onClick={() =>
                                toggleVisibility(
                                  name.includes("confirm") ? "confirm" :
                                  name.includes("new") ? "new" : "current"
                                )
                              }
                            >
                              {showPassword[
                                name.includes("confirm") ? "confirm" :
                                name.includes("new") ? "new" : "current"
                              ] ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          <ErrorMessage
                            name={name}
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      ))}

                      {/* <p className="text-sm text-orange-600 mt-1 cursor-pointer hover:underline">
                        Forgot your password?
                      </p> */}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        {isSubmitting ? "Changing..." : "Change Password"}
                      </button>
                    </Form>
                  )}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChangePasswordDialog;
